<template>
  <div class="popUpWrap">
    <section class="popup light" style="max-width: 560px;">
      <div class="topArea">
        <h4>{{ t('credit-history-top-up-credits') }}</h4>
        <div class="exit" @click.stop="dialogStore.close"></div>
      </div>
      <div class="scrollY">
        <div class="infoBoxWrap">
          <form class="checkout-form flex col gap-16" @submit.prevent>

            <!-- Credit amount -->
            <fieldset class="checkout-form__section">
              <legend class="v-hidden">{{ t('credit-history-top-up-credits-count') }}</legend>

              <div class="form-field mo-col flex item-center justify-between gap-16">
                <div class="form-field__title flex col gap-6">
                  <div class="title">{{ t('credit-history-top-up-credits-count') }}</div>
                  <div class="label label--blue-bg label--blue-text">
                    {{ t('pricing-per-credit', { price: formatAmount(creditUnitPrice) }) }}
                  </div>
                </div>

                <div class="stepper">
                  <div class="stepper__control flex gap-8">
                    <button
                      type="button"
                      class="btn stepper__btn stepper__btn--minus"
                      aria-label="Decrease credit"
                      :disabled="creditCount <= 0"
                      @click.stop="onClickMinus"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M7 11C6.44771 11 6 11.4477 6 12C6 12.5523 6.44771 13 7 13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H7Z" fill="#4E5566"/>
                      </svg>
                    </button>

                    <input
                      type="number"
                      name="credit"
                      class="stepper__input"
                      v-model.number="creditCount"
                      min="1"
                      max="9999"
                      @input="onInputCredit"
                    >

                    <button
                      type="button"
                      class="btn stepper__btn stepper__btn--plus"
                      aria-label="Increase credit"
                      :disabled="creditCount >= 9999"
                      @click.stop="onClickPlus"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 6C11.4477 6 11 6.44772 11 7V11H7C6.44771 11 6 11.4477 6 12C6 12.5523 6.44771 13 7 13H11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H13V7C13 6.44772 12.5523 6 12 6Z" fill="#4E5566"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <p v-if="hasCountError" class="errorMessage"><span class="icon"></span>{{ t('credit-history-top-up-credits-count-error') }}</p>
            </fieldset>

            <!-- Payment Summary -->
            <fieldset class="checkout-form__section">
              <legend class="v-hidden">{{ t('payment-product-price') }}</legend>

              <div class="flex col gap-16">
                <dl class="payment-summary flex col gap-16">
                  <div class="payment-summary__item flex item-center justify-between gap-16">
                    <dt class="payment-summary__label">{{ t('payment-product-price') }}</dt>
                    <dd class="payment-summary__value">{{ formatAmount(subtotal) }}</dd>
                  </div>
                </dl>

                <!-- Total -->
                <div class="payment-total flex col">
                  <div class="payment-total__row flex item-center justify-between gap-16">
                    <strong class="payment-total__label">{{ t('payment-estimated-payment-amount') }}</strong>
                    <strong class="payment-total__amount">{{ formatAmount(subtotal) }}</strong>
                  </div>
                  <p v-if="currency === 'USD'" class="payment-total__tax">{{ t('payment-tax-description') }}</p>
                </div>
              </div>
            </fieldset>

            <div class="p-box flex col gap-16">
              <!-- Notice -->
              <div class="checkout-form__notice">
                <ul class="listDot">
                    <li v-html="convertToNextLine(t('credit-history-top-up-credits-description-1'))" />
                    <li v-if="currency === 'USD'">
                        <i18n-t keypath="credit-history-top-up-credits-description-2">
                            <template #link>
                                <a class="linkTxt" target="_blank" href="/settings/org?tab=PAYMENT">
                                    {{ t('organization-subscription-management') }}
                                </a>
                            </template>
                        </i18n-t>
                    </li>
                </ul>
              </div>

              <!-- Agreement -->
              <div class="checkout-form__agreement">
                <ul>
                  <li>
                    <input id="add-credits-agreement" type="checkbox" name="agreement" v-model="isAgreed">
                    <label for="add-credits-agreement">
                      <i18n-t keypath="credit-history-top-up-credits-agree-check">
                        <template #term>
                          <a class="linkTxt" target="_blank" href="/policy?tab=0">{{ t('common-terms-of-use') }}</a>
                        </template>
                      </i18n-t>
                    </label>
                  </li>
                </ul>
              </div>
            </div>

          </form>
        </div>

        <div class="btnArea">
          <LazySafetyRequestButton
            tag="button"
            type="button"
            class="btn"
            :class="{ disabled: !isFormValid }"
            :safety-click="onClickConfirmPayment"
          >
            {{ t('credit-history-top-up-credits-make-payment') }}
          </LazySafetyRequestButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useToastStore } from "~/store/toast/toastStore";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { useUserStore } from "~/store/user/userStore";
import { useUserService } from "~/composables/service/useUserService";
import { useCreditService } from "~/composables/service/useCreditService";
import { useSubscriptionService } from "~/composables/service/useSubscriptionService";
import { SubscriptionTier } from "~~/package/mapper/subscription/model/SubscriptionTierType";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { SubscriptionStatus } from "~~/package/mapper/subscription/model/SubscriptionStatusType";
import { UserOrgSettingsMenuType } from "~/components/User/Settings/Common/model/UserOrgSettingsMenuType";
import { applicationContext } from '@@/package/application/applicationContext';

const TOSS_ERROR_CODE_SILENT = 'PAY_PROCESS_CANCELED';

const { t } = useI18n();
const router = useRouter();

const { openGenericAlert } = useGenericDialog();
const dialogStore = useDialogStore();
const toastStore = useToastStore();
const userStore = useUserStore();

const creditUnitPrice = ref(0);
const currency = ref('USD');
const orderId = ref('');
const isPricingLoaded = ref(false);

const formatAmount = (amount: number) => {
  const symbol = currency.value === 'KRW' ? '₩' : '$';
  const formatted = currency.value === 'KRW'
    ? amount.toLocaleString('ko-KR')
    : amount.toString();
  return `${symbol}${formatted}`;
};

const creditCount = ref(5);
const isTouched = ref(false);
const isAgreed = ref(false);

const subtotal = computed(() => creditCount.value * creditUnitPrice.value);

const hasCountError = computed(() => isTouched.value && (creditCount.value < 1 || creditCount.value > 9999));
const isFormValid = computed(() => creditCount.value >= 1 && creditCount.value <= 9999 && isAgreed.value && isPricingLoaded.value);

const onClickMinus = () => {
  if (creditCount.value <= 0) return;
  creditCount.value--;
  isTouched.value = true;
};

const onClickPlus = () => {
  if (creditCount.value >= 9999) return;
  creditCount.value++;
  isTouched.value = true;
};

const onInputCredit = () => {
  isTouched.value = true;
  if (creditCount.value < 0) creditCount.value = 0;
  if (creditCount.value > 9999) creditCount.value = 9999;
};

// PAY-LEDGER-05, PAY-SUB-05, PAY-PROC-05: 셀프서비스 크레딧 추가 구매(KRW→토스, 기타→Stripe) → 무기한 PAYMENT_GRANT 지급
const onClickConfirmPayment = async () => {
  if (!isFormValid.value) return;

  if (currency.value === 'KRW') {
    await requestTossPayment();
    return;
  }

  try {
    await useCreditService().purchaseCredits({ creditCount: creditCount.value });
    await useUserService().setUser();
    toastStore.openToast({
      toastType: ToastType.SUCCESS,
      contents: t('credit-history-top-up-credits-success'),
    });
    dialogStore.options.confirm?.event?.();
    dialogStore.close();
  } catch {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('credit-history-top-up-credits-fail'),
    });
  }
};

const requestTossPayment = async () => {
  // useRuntimeConfig() returns the build-time baked config (live). Use the
  // hostname-resolved config so stage/eu/kr get their own Toss client key.
  const config = { public: applicationContext.getConfig() };

  try {
    const tossPayments = await loadTossPayments(config.public.TOSS_PAYMENT_CLIENT_KEY as string);
    const payment = tossPayments.payment({ customerKey: userStore.userV2.uuid });
    const baseUrl = `${window.location.origin}/settings/org?tab=${UserOrgSettingsMenuType.CreditManagement}&tossCredit=1&creditCount=${creditCount.value}`;

    await payment.requestPayment({
      method: 'CARD',
      amount: {
        currency: 'KRW',
        value: subtotal.value,
      },
      orderId: orderId.value,
      orderName: t('credit-history-top-up-credits'),
      successUrl: baseUrl,
      failUrl: baseUrl,
      customerEmail: userStore.userV2.email,
      customerName: userStore.userV2.fullName,
      card: {
        useEscrow: false,
        flowMode: 'DEFAULT',
        useCardPoint: false,
        useAppCardOnly: false,
      },
    });
  } catch (error: any) {
    if (error?.code !== TOSS_ERROR_CODE_SILENT) {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t('credit-history-top-up-credits-fail'),
      });
    }
  }
};

onMounted(async () => {
  if(!userStore.subscription) {
    openGenericAlert(
      {
        title: t("credit-history-top-up-credits-restriction-info-1-title"),
        contents: t("organization-subscription-none-description-2")
      },
      {
        confirm: {
          title: t("organization-subscription-none-button"),
          event: () => {
            router.replace('/home/pricing');
          }
        }
      }
    );
  }
  else if(userStore.subscription?.status === SubscriptionStatus.INACTIVE) {
    openGenericAlert(
      {
        title: t("credit-history-top-up-credits-restriction-info-2-title"),
        contents: t("credit-history-top-up-credits-restriction-info-2-description")
      },
      {
        confirm: {
          title: t("payment-complete-button"),
          event: () => {
            router.replace({ path: '/settings/org', query: { tab: UserOrgSettingsMenuType.PAYMENT } });
          }
        }
      }
    );
  }
  else if(userStore.subscription?.planTier === SubscriptionTier.ENTERPRISE) {
    const linkDom = `<a class="link" href="/home/contact">${t("common-contact-us")}</a>`

    openGenericAlert(
      {
        title: t("credit-history-top-up-credits-restriction-info-3-title"),
        contents: t("credit-limit-description", { linkDom })
      },
      {
        confirm: {
          title: t("common-contact-us"),
          event: () => {
            router.replace({ path: '/home/contact' });
          }
        }
      }
    );
  }
  else {
    try {
      const pricing = await useSubscriptionService().getCreditPricing();
      creditUnitPrice.value = pricing.creditUnitPrice;
      currency.value = pricing.currency;
      orderId.value = crypto.randomUUID();
      isPricingLoaded.value = true;
    } catch {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t('credit-history-top-up-credits-fail'),
      });
    }
  }
})
</script>
